// Reporting calendar (V17-V20, E05, rule 18).
import { useMutation } from "convex/react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Doc } from "../../../convex/_generated/dataModel";
import { useProject } from "@/hooks/project";
import { toDay } from "@/engine/dates";
import { Badge, Button, Card, Field, Input, PageHeader, fmtNum, useRun, useToast } from "@/components/ui";
import { DataGrid, type Col } from "@/components/DataGrid";
import { ExportButton, ImportButton } from "@/components/Importer";
import { exportCalendar, parseCalendar } from "@/lib/formats";
import { checkCalendar, generateCalendar } from "@/engine/calendar";
import { sortedPeriods } from "@/shared/model";

export function CalendarPage() {
  const { project, periods, canEdit, currentIdx } = useProject();
  const generate = useMutation(api.setup.generate);
  const setCalendar = useMutation(api.setup.setCalendar);
  const updatePeriod = useMutation(api.setup.updatePeriod);
  const clear = useMutation(api.setup.clearCalendar);
  const run = useRun();
  const toast = useToast();
  const [g, setG] = useState({ start: "", end: "", lengthDays: 7, stdHours: 40 });
  const [defHours, setDefHours] = useState(40);
  const preview = g.start && g.end && g.end >= g.start ? generateCalendar({ ...g, lengthDays: Math.max(1, g.lengthDays) }).length : 0;
  const cols: Col<Doc<"periods">>[] = [
    { id: "n", title: "Period", width: 70, kind: "number", get: (r) => r.number },
    { id: "start", title: "Start", width: 100, kind: "date", get: (r) => r.start },
    { id: "cutoff", title: "Cutoff", width: 100, kind: "date", get: (r) => r.cutoff },
    { id: "hours", title: "Std hours", width: 90, kind: "number", get: (r) => r.stdHours, set: canEdit ? (r, v) => void run(() => updatePeriod({ id: r._id, stdHours: Number(v) })) : undefined, readonly: (r) => r.closed },
    { id: "me", title: "Month end", width: 90, kind: "bool", get: (r) => r.monthEnd, set: canEdit ? (r, v) => void run(() => updatePeriod({ id: r._id, monthEnd: !!v })) : undefined },
    { id: "st", title: "Status", width: 90, get: (r) => (r.closed ? "closed" : periods[currentIdx]?._id === r._id ? "current" : "open"), tone: (r) => (r.closed ? "muted" : periods[currentIdx]?._id === r._id ? "derived" : undefined) },
  ];
  return (
    <div className="max-w-4xl space-y-4">
      <PageHeader
        title="Reporting calendar"
        sub="Periods with cutoffs and standard hours. Tick Month End on the cutoffs the monthly charts should use. The calendar never extends by itself; dates outside it are errors."
        actions={
          periods.length > 0 && (
            <>
              <ExportButton file={`${project.code}-calendar.xlsx`} sheets={() => [exportCalendar(sortedPeriods(periods))]} />
              {canEdit && !project.baselineLocked && !periods.some((p) => p.closed) && (
                <Button variant="danger" onClick={() => confirm("Clear the whole calendar?") && run(() => clear({ projectId: project._id }), "Calendar cleared")}>
                  Clear
                </Button>
              )}
            </>
          )
        }
      />
      {periods.length === 0 && canEdit && (
        <Card title="Generate calendar">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Start">
              <Input type="date" value={g.start} onChange={(e) => setG({ ...g, start: e.target.value })} />
            </Field>
            <Field label="End">
              <Input type="date" value={g.end} onChange={(e) => setG({ ...g, end: e.target.value })} />
            </Field>
            <Field label="Period length (days)" hint="7 = weekly, 14 = two weeks">
              <Input
                type="number"
                min={1}
                value={g.lengthDays}
                onChange={(e) => {
                  // Keep 40 h a week unless the hours were changed by hand (Cesar's 2-week test kept 40 and doubled his headcount).
                  const lengthDays = Number(e.target.value);
                  const followed = g.stdHours === Math.round((40 * g.lengthDays) / 7);
                  setG({ ...g, lengthDays, stdHours: followed && lengthDays > 0 ? Math.round((40 * lengthDays) / 7) : g.stdHours });
                }}
              />
            </Field>
            <Field label="Standard hours per period" hint={`One person's hours in a whole period${g.lengthDays > 0 ? ` (${fmtNum((g.stdHours * 7) / g.lengthDays, 1)} h a week)` : ""}`}>
              <Input type="number" min={1} value={g.stdHours} onChange={(e) => setG({ ...g, stdHours: Number(e.target.value) })} />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button variant="primary" disabled={!preview} onClick={() => run(() => generate({ projectId: project._id, ...g }), `Generated ${preview} periods`)}>
              Generate {preview ? `${preview} periods` : ""}
            </Button>
            <span className="text-xs text-slate-500">The last cutoff of each month is pre-ticked as Month End. Change it on any row.</span>
          </div>
          <div className="mt-4 flex items-end gap-2 border-t border-slate-100 pt-4">
            <Field label="Default std hours for files without them">
              <Input type="number" value={defHours} onChange={(e) => setDefHours(Number(e.target.value))} className="w-32" />
            </Field>
            <ImportButton
              label="Import calendar"
              build={(sheets) => {
                const { periods: ps, errors } = parseCalendar(sheets[0].rows, defHours);
                const issues = checkCalendar(ps);
                const hard = errors.filter((e) => !/assumed to start/.test(e.message));
                return {
                  problems: [...hard.map((e) => `Row ${e.line}: ${e.message}`), ...issues.map((i) => `${i.kind} at period ${i.period}`)],
                  note: errors.filter((e) => /assumed/.test(e.message)).map((e) => <p key={e.line} className="rounded bg-amber-50 p-2 text-sm text-amber-800">{e.message}</p>),
                  rows: ps.map((p) => ({ line: p.number, key: `P${p.number}`, status: "new", detail: `${p.start} to ${p.cutoff}, ${p.stdHours} h${p.monthEnd ? ", month end" : ""}` })),
                  commit: () => setCalendar({ projectId: project._id, periods: ps }),
                };
              }}
            />
          </div>
        </Card>
      )}
      {periods.length > 0 && (
        <>
          <div className="flex gap-2 text-sm text-slate-600">
            <Badge>{periods.length} periods</Badge>
            <Badge>
              {periods[0].start} to {periods[periods.length - 1].cutoff}
            </Badge>
            <Badge tone="blue">{periods.filter((p) => p.monthEnd).length} month ends</Badge>
            <Badge tone="slate">{periods.filter((p) => p.closed).length} closed</Badge>
          </div>
          {(() => {
            // Flag periods whose standard hours work out under 30 h a week: usually a weekly figure on a longer period.
            const low = periods.filter((p) => {
              const days = toDay(p.cutoff) - toDay(p.start) + 1;
              return days >= 10 && (p.stdHours * 7) / days < 30;
            });
            return low.length > 0 ? (
              <p className="rounded bg-amber-50 p-2 text-sm text-amber-800">
                {low.length} period{low.length > 1 ? "s are" : " is"} longer than a week but {low.length > 1 ? "have" : "has"} under 30 standard hours a week (e.g. P{low[0].number}: {low[0].stdHours} h over{" "}
                {toDay(low[0].cutoff) - toDay(low[0].start) + 1} days). Standard hours are per period, so a two-week period at 40 h a week is 80. The histogram divides by this number.
              </p>
            ) : null;
          })()}
          <DataGrid rows={periods} cols={cols} height={560} onEdit={(edits) => edits.forEach((e) => e.col.set?.(e.row, e.value))} onError={(m) => toast({ text: m, tone: "error" })} />
        </>
      )}
    </div>
  );
}
